// src/components/AssignSingleMemberPhotoPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { auth, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { Container, Row, Col, Card, Spinner, Alert, Breadcrumb, Button, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import MemberPhotoAvatar from './MemberPhotoAvatar';

// Helper to get orgId from the current user's token claims
async function getOrgId() {
    if (!auth.currentUser) return null;
    try {
        const token = await auth.currentUser.getIdTokenResult(false);
        return token.claims.orgId || null;
    } catch (error) {
        console.error("Error getting user organization ID:", error);
        return null;
    }
}

// --- Main Component ---
function AssignSingleMemberPhotoPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { familyId } = useParams();

    // Data passed from the previous page. forMemberId is optional.
    const { forMemberName, forMemberId, currentPhotoPath, returnPath } = location.state || {};

    const [extractedPhotos, setExtractedPhotos] = useState([]);
    const [selectedGridPhotoPath, setSelectedGridPhotoPath] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // Initialize Firebase Cloud Function
    const firebaseFunctions = getFunctions();
    const updateMemberV2Func = httpsCallable(firebaseFunctions, 'updateMemberV2');

    // Effect to fetch available photos from Firebase Storage
    useEffect(() => {
        let isMounted = true;
        const fetchPhotos = async () => {
            if (!familyId) {
                if (isMounted) { setError("Family ID is missing from the URL."); setLoading(false); }
                return;
            }
            const orgId = await getOrgId();
            if (!orgId) {
                if (isMounted) { setError("Could not verify your organization. Please log in again."); setLoading(false); }
                return;
            }
            setLoading(true);
            try {
                const photosPath = `${orgId}/family/${familyId}/members/`;
                const listResult = await listAll(ref(storage, photosPath));
                const photosDataPromises = listResult.items.map(async (itemRef) => ({
                    path: itemRef.fullPath,
                    url: await getDownloadURL(itemRef),
                }));
                const photosData = await Promise.all(photosDataPromises);
                if (isMounted) setExtractedPhotos(photosData);
            } catch (e) {
                if (e.code === 'storage/object-not-found') {
                    if (isMounted) setExtractedPhotos([]);
                } else {
                    console.error("Error fetching extracted photos:", e);
                    if (isMounted) setError("Error loading available photos.");
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchPhotos();
        return () => { isMounted = false; };
    }, [familyId]);

    // --- Action Handlers ---

    // This function now handles both ADD and EDIT scenarios
    const handleConfirmSelection = async (newPhotoPath) => {
        if (!returnPath) return;
        setIsUpdating(true);
        setError(null);

        // If forMemberId exists, we are EDITING an existing member. Update DB directly.
        if (forMemberId) {
            try {
                const result = await updateMemberV2Func({ memberId: forMemberId, photo: newPhotoPath });
                if (!result.data.success) {
                    throw new Error(result.data.message || 'Failed to update photo on the server.');
                }
                // Navigate back and tell the previous page to refresh
                navigate(returnPath, { state: { photoUpdated: true } });
            } catch (err) {
                setError(err.message || "An unexpected error occurred while updating the photo.");
                setIsUpdating(false);
            }
        } else {
            // If no forMemberId, we are ADDING a new member. Just pass the path back.
            navigate(returnPath, { state: { ...location.state, newPhotoPath: newPhotoPath } });
        }
    };

    const handleSetNewPhoto = () => {
        if (!selectedGridPhotoPath) return;
        handleConfirmSelection(selectedGridPhotoPath);
    };

    const handleRemovePhoto = () => {
        const confirm = window.confirm(`Are you sure you want to remove the photo for ${forMemberName}?`);
        if (confirm) {
            handleConfirmSelection(null); // Pass null to remove the photo
        }
    };

    // --- Render Logic ---

    // Guard clause now allows forMemberId to be missing.
    if (!forMemberName || !returnPath) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">Required data (Member Name or Return Path) was not provided. Please go back and try again.</Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-3">
            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                    <Breadcrumb>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/families/${familyId}` }}>Family</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: returnPath }}>{forMemberId ? 'Edit Member' : 'Add Member'}</Breadcrumb.Item>
                        <Breadcrumb.Item active>Assign Photo</Breadcrumb.Item>
                    </Breadcrumb>
                    <Card className="shadow-sm">
                        <Card.Header as="h3" className="text-center">Set Photo for {forMemberName}</Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                            <div className="text-center mb-3">
                                <p className="mb-2">Current Photo:</p>
                                <MemberPhotoAvatar src={currentPhotoPath} alt={`Current photo of ${forMemberName}`} size={120} />
                                {currentPhotoPath && (
                                    <Button variant="link" className="text-danger d-block mx-auto mt-2" onClick={handleRemovePhoto} disabled={isUpdating}>
                                        <FontAwesomeIcon icon={faTrash} className="me-1" /> Remove Current Photo
                                    </Button>
                                )}
                            </div>
                            <hr />
                            <h4 className="mt-4 mb-3">Select from Available Family Photos</h4>
                            {loading ? (
                                <div className="text-center"><Spinner animation="border" /></div>
                            ) : extractedPhotos.length === 0 ? (
                                <Alert variant="info">No other photos are available for this family. Upload photos to the family page to select them here.</Alert>
                            ) : (
                                <Row className="g-2">
                                    {extractedPhotos.map(photo => (
                                        <Col xs={3} sm={2} key={photo.path}>
                                            <div
                                                onClick={() => setSelectedGridPhotoPath(photo.path)}
                                                style={{
                                                    cursor: 'pointer',
                                                    border: selectedGridPhotoPath === photo.path ? '3px solid #0d6efd' : '3px solid transparent',
                                                    padding: '2px',
                                                    borderRadius: '8px',
                                                    transition: 'border-color 0.2s'
                                                }}
                                            >
                                                <Image src={photo.url} alt="Selectable photo" fluid rounded />
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                            <div className="d-grid mt-4">
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={handleSetNewPhoto}
                                    disabled={!selectedGridPhotoPath || isUpdating || loading}
                                >
                                    {isUpdating ? <><Spinner as="span" size="sm" /> Saving...</> : 'Set as Member Photo'}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default AssignSingleMemberPhotoPage;
